import React, { useState } from 'react';

import { cn } from '@poseidon/components/cn';
import { Badge } from '@poseidon/components/Badge';
import { Button } from '@poseidon/components/Input2';
import { Arrows, Communication, General } from '@poseidon/components/StrokeIcons';
import { WaveSpinner } from '@poseidon/components/WaveSpinner';
import { SmallPaddedScrollable } from '@poseidon/components/Scrollable';

import { api } from '@/utils/api';

type RequiredActionsListViewProps = {
  contactId: string;
  onBack: () => void;
};

function getStatusBadge(status: string) {
  switch (status) {
    case 'pending':
      return (
        <Badge size="xs" type="warning" dot>
          Pending
        </Badge>
      );
    case 'completed':
      return (
        <Badge size="xs" type="success" dot>
          Completed
        </Badge>
      );
    case 'dismissed':
      return (
        <Badge size="xs" type="standard" dot>
          Dismissed
        </Badge>
      );
    default:
      return (
        <Badge size="xs" type="standard" dot>
          {status}
        </Badge>
      );
  }
}

function formatDate(date: Date | string | null | undefined): string {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function MessageCard({
  label,
  direction,
  body,
  date,
  variant,
}: {
  label: string;
  direction: string;
  body: string;
  date: Date | string | null | undefined;
  variant: 'source' | 'completed';
}) {
  const [expanded, setExpanded] = useState(false);
  const isSent = direction === 'sent';
  const needsTruncation = body.length > 120;
  const displayBody = expanded || !needsTruncation ? body : body.slice(0, 120) + '...';

  const borderColor = variant === 'source'
    ? 'border-blue-100 dark:border-blue-800'
    : 'border-green-100 dark:border-green-800';
  const bgColor = variant === 'source'
    ? 'bg-blue-50/60 dark:bg-blue-900/10'
    : 'bg-green-50/60 dark:bg-green-900/10';
  const labelColor = variant === 'source'
    ? 'text-blue-600 dark:text-blue-400'
    : 'text-green-600 dark:text-green-400';

  return (
    <div
      onClick={needsTruncation ? () => setExpanded((v) => !v) : undefined}
      className={cn(
        'mt-1 rounded-sm border p-2',
        borderColor,
        bgColor,
        needsTruncation && 'cursor-pointer hover:opacity-80',
      )}
    >
      <div className={cn('mb-1 flex items-center gap-1.5 text-xs font-medium', labelColor)}>
        {isSent && <Communication.Send01 className="size-3" />}
        {!isSent && <Arrows.CornerUpLeft className="size-3" />}
        <span>{label} {isSent ? 'sent' : 'received'} message</span>
        <span className="text-slate-400 dark:text-slate-500">{formatDate(date)}</span>
      </div>
      <p className="whitespace-pre-wrap text-xs text-slate-700 dark:text-slate-300">
        {displayBody}
      </p>
      {needsTruncation && !expanded && (
        <span className="mt-1 inline-block text-xs font-medium text-blue-500 dark:text-blue-400">
          Click to expand
        </span>
      )}
    </div>
  );
}

export function RequiredActionsListView({ contactId, onBack }: RequiredActionsListViewProps) {
  const actionsQuery = api.contact.requiredActions.useQuery(
    { contactId },
    { enabled: Boolean(contactId) },
  );

  if (actionsQuery.isPending) {
    return (
      <SmallPaddedScrollable className="flex items-center justify-center">
        <WaveSpinner />
      </SmallPaddedScrollable>
    );
  }

  const actions = actionsQuery.data ?? [];

  if (actions.length === 0) {
    return (
      <SmallPaddedScrollable className="flex flex-col items-center justify-center gap-2 text-slate-500 dark:text-slate-400">
        <General.CheckCircle className="size-8 opacity-50" />
        <p className="text-sm">No actions for this contact</p>
        <Button size="sm" onClick={onBack} icon={<Arrows.ArrowLeft />}>
          Back to Contact Info
        </Button>
      </SmallPaddedScrollable>
    );
  }

  return (
    <SmallPaddedScrollable innerClassName="p-0">
      {/* Header with back button */}
      <div className="sticky top-0 z-10 border-b border-slate-200 bg-white px-4 py-3 dark:border-slate-700 dark:bg-slate-900">
        <div className="flex items-center gap-2">
          <Button
            size="xs"
            color="blue" tone="outline"
            onClick={onBack}
            icon={<Arrows.ArrowLeft />}
          />
          <span className="font-medium text-slate-900 dark:text-slate-100">
            Actions Needed
          </span>
        </div>
      </div>
      <div className="flex flex-col">
        {actions.map((action) => (
          <div
            key={action.publicId}
            className={cn(
              'flex flex-col gap-2 border-b border-slate-200 px-4 py-3 dark:border-slate-700',
              action.status === 'pending' && 'bg-yellow-50/50 dark:bg-yellow-900/10',
            )}
          >
            {/* Action text + status */}
            <div className="flex items-start justify-between gap-2">
              <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
                {action.actionText}
              </span>
              {getStatusBadge(action.status)}
            </div>

            {/* Created timestamp + source */}
            <div className="flex items-center gap-3 text-xs text-slate-500 dark:text-slate-400">
              <span>Created: {formatDate(action.createdAt)}</span>
              <span className="capitalize">{action.source}</span>
            </div>

            {/* Source message — the message that triggered this action */}
            {action.sourceBody && (
              <MessageCard
                label="Triggered by"
                direction={action.sourceDirection}
                body={action.sourceBody}
                date={action.sourceDate}
                variant="source"
              />
            )}

            {/* Completed message */}
            {action.status === 'completed' && action.completedBody && (
              <MessageCard
                label="Completed by"
                direction="sent"
                body={action.completedBody}
                date={action.completedDate}
                variant="completed"
              />
            )}
          </div>
        ))}
      </div>
    </SmallPaddedScrollable>
  );
}
